import { Navigate, Route, Routes } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Register from "./pages/Register";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Library from "./pages/Library";
import Entry from "./pages/Entry";
import Search from "./pages/Search";
import Account from "./pages/Account";
import Record from "./pages/Record";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";

export type User = {
  id: string;
  email: string;
  authSource?: "password" | "access";
  /** Effective IANA zone for dates (override or IP-detected, else UTC). */
  displayTimezone: string;
  timezoneOverride: string | null;
  timezoneDetected: string | null;
  /** 0 = unlimited */
  maxRecordingsPer24h: number;
  /** 0 = unlimited */
  maxRecordingDurationSecs: number;
};

function normalizeUser(
  u: {
    id: string;
    email: string;
    authSource?: "password" | "access";
    displayTimezone?: string;
    timezoneOverride?: string | null;
    timezoneDetected?: string | null;
    maxRecordingsPer24h?: number;
    maxRecordingDurationSecs?: number;
  } | null,
): User | null {
  if (!u) return null;
  return {
    id: u.id,
    email: u.email,
    authSource: u.authSource ?? "password",
    displayTimezone: u.displayTimezone ?? "UTC",
    timezoneOverride: u.timezoneOverride ?? null,
    timezoneDetected: u.timezoneDetected ?? null,
    maxRecordingsPer24h: u.maxRecordingsPer24h ?? 0,
    maxRecordingDurationSecs: u.maxRecordingDurationSecs ?? 0,
  };
}

export default function App() {
  const [user, setUser] = useState<User | null | undefined>(undefined);

  const clearSession = useCallback(async () => {
    await api("/api/auth/logout", { method: "POST", body: "{}" });
    setUser(null);
  }, []);

  useEffect(() => {
    (async () => {
      const r = await api<{ user: User | null }>("/api/me");
      if (r.ok && r.data?.user) setUser(normalizeUser(r.data.user));
      else setUser(null);
    })();
  }, []);

  return (
    <Routes>
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route
        path="/login"
        element={
          user ? (
            <Navigate to="/record" replace />
          ) : (
            <Login onLogin={(u) => setUser(normalizeUser(u))} />
          )
        }
      />
      <Route
        path="/register"
        element={
          user ? (
            <Navigate to="/record" replace />
          ) : (
            <Register />
          )
        }
      />
      <Route
        path="/forgot-password"
        element={user ? <Navigate to="/record" replace /> : <ForgotPassword />}
      />
      {/* Must not redirect when logged in: users often still have a session when opening the email link. */}
      <Route
        path="/reset-password"
        element={<ResetPassword onLogin={(u) => setUser(normalizeUser(u))} />}
      />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route
        element={
          user === undefined ? (
            <div className="layout">
              <div className="layout-main">
                <div className="skeleton" style={{ height: 24, width: 180 }} />
              </div>
            </div>
          ) : user ? (
            <Layout user={user} onLogout={clearSession} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      >
        <Route path="/record" element={<Record />} />
        <Route path="/library" element={<Library />} />
        <Route path="/entry/:id" element={<Entry />} />
        <Route path="/search" element={<Search />} />
        <Route
          path="/account"
          element={
            <Account
              user={user!}
              onLogout={clearSession}
              onUserUpdated={(u) => setUser(normalizeUser(u))}
            />
          }
        />
      </Route>
      <Route
        path="/"
        element={
          user === undefined ? (
            <div className="layout">
              <div className="layout-main">
                <div className="skeleton" style={{ height: 24, width: 180 }} />
              </div>
            </div>
          ) : (
            <Navigate to={user ? "/record" : "/login"} replace />
          )
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
